/**
 * UUID generation.
 *
 * The master prompt prefers UUID v7 with v4 as a fallback. v7 matters here for
 * a concrete reason rather than fashion: its leading 48 bits are a
 * millisecond timestamp, so v7 keys are time-ordered. On the high-insert
 * tables in this platform — `audit_logs`, `availability_calendar`,
 * `integration_logs` — random v4 keys scatter B-tree inserts across the whole
 * index and shred write locality. v7 appends.
 *
 * Implemented directly rather than pulled from a dependency: the layout is
 * fixed by RFC 9562 and is a dozen lines.
 *
 * Uses the Web Crypto API (`globalThis.crypto`) which is available in both
 * Node.js 19+ and all modern browsers, so the package is isomorphic.
 */
const UUID_BYTE_LENGTH = 16;
const VERSION_BYTE_INDEX = 6;
const VARIANT_BYTE_INDEX = 8;
const TIMESTAMP_BYTE_LENGTH = 6;
const BITS_PER_BYTE = 8;
const LOW_NIBBLE_MASK = 0x0f;
const VERSION_7_BITS = 0x70;
const VARIANT_MASK = 0x3f;
const VARIANT_RFC9562_BITS = 0x80;
const BYTE_MASK = 0xff;
function getRandomBytes(length) {
    const bytes = new Uint8Array(length);
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
}
/**
 * Generates a UUID v7: 48-bit big-endian Unix millisecond timestamp, version
 * nibble, 12 random bits, variant bits, 62 random bits.
 */
export function uuidV7(now = Date.now()) {
    const bytes = getRandomBytes(UUID_BYTE_LENGTH);
    // 48-bit big-endian timestamp across bytes 0..5.
    let remaining = BigInt(now);
    for (let index = TIMESTAMP_BYTE_LENGTH - 1; index >= 0; index -= 1) {
        bytes[index] = Number(remaining & BigInt(BYTE_MASK));
        remaining >>= BigInt(BITS_PER_BYTE);
    }
    // Version 7 in the high nibble of byte 6, keeping the random low nibble.
    bytes[VERSION_BYTE_INDEX] = ((bytes[VERSION_BYTE_INDEX] ?? 0) & LOW_NIBBLE_MASK) | VERSION_7_BITS;
    // RFC 9562 variant (10xx) in the top bits of byte 8.
    bytes[VARIANT_BYTE_INDEX] =
        ((bytes[VARIANT_BYTE_INDEX] ?? 0) & VARIANT_MASK) | VARIANT_RFC9562_BITS;
    return formatUuid(bytes);
}
/** Generates a UUID v4. The fallback where time-ordering is unwanted. */
export function uuidV4() {
    return globalThis.crypto.randomUUID();
}
/**
 * The platform's default identifier generator.
 *
 * Every business table's primary key comes from here. Centralising it means
 * switching generation strategy is one edit, not a repository-wide sweep.
 */
export function generateId() {
    return uuidV7();
}
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
/** Whether a string is a syntactically valid RFC 9562 UUID. */
export function isUuid(value) {
    return UUID_PATTERN.test(value);
}
const VERSION_CHAR_INDEX = 14;
const RADIX_HEX = 16;
/** Extracts the version nibble, or null if the value is not a UUID. */
export function uuidVersion(value) {
    if (!isUuid(value))
        return null;
    return Number.parseInt(value[VERSION_CHAR_INDEX] ?? '', RADIX_HEX);
}
const UUID_V7 = 7;
const TIMESTAMP_HEX_LENGTH = 12;
const HEX_RADIX = 16;
/**
 * Reads the embedded timestamp out of a UUID v7.
 *
 * Returns null for any other version — useful for partition routing and for
 * asserting in tests that ordering actually holds.
 */
export function uuidV7Timestamp(value) {
    if (uuidVersion(value) !== UUID_V7)
        return null;
    const hex = value.replace(/-/gu, '').slice(0, TIMESTAMP_HEX_LENGTH);
    return new Date(Number.parseInt(hex, RADIX_HEX));
}
/*
 * RFC 9562 fixes the textual form as 8-4-4-4-12 hex characters. These offsets
 * are a wire-format specification, not tunable constants, so they are written
 * literally where the reader can check them against the RFC.
 */
/* eslint-disable @typescript-eslint/no-magic-numbers */
function formatUuid(bytes) {
    const hex = Array.from(bytes, (byte) => byte.toString(HEX_RADIX).padStart(2, '0')).join('');
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20, 32),
    ].join('-');
}
/* eslint-enable @typescript-eslint/no-magic-numbers */
//# sourceMappingURL=uuid.js.map